const SECTIONS = [
  { id: "agreement", label: "1. Agreement to terms" },
  { id: "eligibility", label: "2. Eligibility" },
  { id: "accounts", label: "3. User accounts" },
  { id: "subscription", label: "4. Subscription and payment terms" },
  { id: "ip", label: "5. Intellectual property" },
  { id: "user-content", label: "6. User content and submissions" },
  { id: "prohibited", label: "7. Prohibited activities" },
  { id: "third-party", label: "8. Third-party services" },
  { id: "ai", label: "9. AI-generated content disclaimer" },
  { id: "fantasy", label: "10. Fantasy sports disclaimer" },
  { id: "warranties", label: "11. Disclaimer of warranties" },
  { id: "liability", label: "12. Limitation of liability" },
  { id: "indemnification", label: "13. Indemnification" },
  { id: "termination", label: "14. Termination" },
  { id: "governing-law", label: "15. Governing law and dispute resolution" },
  { id: "changes", label: "16. Changes to terms" },
  { id: "contact", label: "17. Contact us" },
];

export function TermsOfServiceContent() {
  return (
    <article className="booth-legal">
      <header className="booth-legal-header">
        <h1>Terms of service</h1>
        <p className="booth-legal-subtitle">Last updated April 10, 2026</p>
      </header>

      <p>
        These Terms of Service ("<strong>Terms</strong>") constitute a legally binding agreement between you and
        The Booth ("<strong>we</strong>," "<strong>us</strong>," or "<strong>our</strong>") governing your access
        to and use of our website at{" "}
        <a rel="noopener noreferrer" target="_blank" href="https://thebooth.gg">
          https://thebooth.gg
        </a>
        {" "}and our AI-powered fantasy sports coverage service (collectively, the "<strong>Services</strong>").
      </p>

      <p>
        <strong>Please read these Terms carefully before using our Services.</strong>{" "}
        By accessing or using the Services, you confirm that you have read, understood, and agree to be bound
        by these Terms. If you do not agree, you must not access or use the Services.
      </p>

      <p>
        <strong>Questions or concerns?</strong>{" "}
        Contact us at{" "}
        <a rel="noopener noreferrer" target="_blank" href="mailto:admin@thebooth.gg">
          admin@thebooth.gg
        </a>
        .
      </p>

      <nav id="toc" className="booth-legal-toc" aria-label="Table of contents">
        <h2>Table of contents</h2>
        <ol>
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`}>{s.label}</a>
            </li>
          ))}
        </ol>
      </nav>

      <section id="agreement">
        <h2>1. Agreement to terms</h2>
        <p>
          By accessing or using the Services, you represent that you have the legal capacity to enter into a
          binding contract and that you agree to comply with these Terms. Your continued use of the Services
          following any update to these Terms constitutes acceptance of the revised Terms.
        </p>
      </section>

      <section id="eligibility">
        <h2>2. Eligibility</h2>
        <p>
          You must be at least 18 years of age to use the Services. By using the Services, you represent and
          warrant that you are 18 years of age or older. If you are under 18, you are not permitted to register
          for an account or use the Services.
        </p>
        <p>
          The Services are intended for users located in the United States. If you access the Services from
          outside the United States, you do so at your own risk and are responsible for compliance with local
          laws.
        </p>
      </section>

      <section id="accounts">
        <h2>3. User accounts</h2>
        <p>
          To access certain features of the Services, you must register for an account. When creating your
          account, you agree to:
        </p>
        <ul>
          <li>Provide accurate, current, and complete information during registration</li>
          <li>Maintain and promptly update your account information to keep it accurate and complete</li>
          <li>
            Maintain the security of your password and accept responsibility for all activities that occur
            under your account
          </li>
          <li>
            Notify us immediately at{" "}
            <a rel="noopener noreferrer" target="_blank" href="mailto:admin@thebooth.gg">
              admin@thebooth.gg
            </a>
            {" "}of any unauthorized use of your account or any other breach of security
          </li>
          <li>Not share your account credentials with any other person or entity</li>
        </ul>
        <p>
          We reserve the right to disable or delete any account at our discretion, including if we believe you
          have violated these Terms.
        </p>
      </section>

      <section id="subscription">
        <h2>4. Subscription and payment terms</h2>

        <h3>Beta period — currently free</h3>
        <p>
          The Booth is currently free to use while in beta. We may introduce paid subscription plans in the
          future as the product develops. <strong>You will not be charged at any time without your explicit
          prior consent.</strong> Before any pricing changes take effect, we will notify you by email at the
          address associated with your account, giving you the opportunity to review the new terms and decide
          whether to continue using the Services.
        </p>

        <h3>Future billing</h3>
        <p>
          If and when paid plans are introduced, billing details, pricing, and frequencies will be clearly
          disclosed before you are asked to provide any payment information. Paid subscriptions, if offered,
          will be processed securely through our payment processor, Stripe. You will always have the
          opportunity to cancel before being charged.
        </p>

        <h3>Email communications</h3>
        <p>
          By creating an account, you agree to receive weekly AI-generated fantasy league recap emails as part
          of the Services. You may unsubscribe from these emails at any time by using the unsubscribe link
          included in each email or by contacting us at{" "}
          <a rel="noopener noreferrer" target="_blank" href="mailto:admin@thebooth.gg">
            admin@thebooth.gg
          </a>
          . Unsubscribing from marketing emails does not cancel your account or subscription.
        </p>
      </section>

      <section id="ip">
        <h2>5. Intellectual property</h2>
        <p>
          The Services and all content included within them — including but not limited to text, graphics,
          logos, AI-generated writeups, software, and data compilations — are the property of The Booth or its
          licensors and are protected by applicable intellectual property laws.
        </p>
        <p>
          You are granted a limited, non-exclusive, non-transferable, revocable license to access and use the
          Services for your personal, non-commercial purposes. You may not:
        </p>
        <ul>
          <li>
            Copy, reproduce, distribute, or create derivative works from any content in the Services without
            our prior written consent
          </li>
          <li>Use any content from the Services for commercial purposes</li>
          <li>Remove or alter any proprietary notices or labels on the Services</li>
        </ul>
      </section>

      <section id="user-content">
        <h2>6. User content and submissions</h2>
        <p>
          You may submit content to the Services, including fantasy league information and member email
          addresses. By submitting content, you:
        </p>
        <ul>
          <li>Represent that you own or have the necessary rights to submit such content</li>
          <li>
            Grant us a worldwide, non-exclusive, royalty-free license to use, process, and display such content
            solely to provide the Services
          </li>
          <li>
            Confirm that submitting the content does not violate any third-party rights or applicable laws,
            including the privacy rights of league members whose email addresses you provide
          </li>
        </ul>
        <p>
          You are solely responsible for ensuring that any email addresses you submit belong to individuals who
          have consented to receive emails from The Booth.
        </p>
      </section>

      <section id="prohibited">
        <h2>7. Prohibited activities</h2>
        <p>You agree not to engage in any of the following activities in connection with the Services:</p>
        <ul>
          <li>Violating any applicable federal, state, or local laws or regulations</li>
          <li>Engaging in any fraudulent, deceptive, or harmful activity</li>
          <li>
            Attempting to gain unauthorized access to any part of the Services, our servers, or any system or
            network connected to the Services
          </li>
          <li>Using automated scripts, bots, scrapers, or other means to access or collect data from the Services</li>
          <li>Interfering with or disrupting the integrity or performance of the Services</li>
          <li>Submitting false or misleading information, including inaccurate league member email addresses</li>
          <li>Using the Services to transmit unsolicited commercial communications (spam)</li>
        </ul>
      </section>

      <section id="third-party">
        <h2>8. Third-party services</h2>
        <p>
          The Services integrate with or rely upon third-party platforms and services, including ESPN Fantasy
          Sports, Stripe (payment processing), Supabase (data storage), and OpenAI (AI content generation). Your
          use of the Services may be subject to the terms and privacy policies of these third parties.
        </p>
        <p>
          We are not responsible for the availability, accuracy, or content of third-party services, and we
          disclaim all liability arising from your use of or reliance on any third-party service. Links to
          third-party websites or services do not constitute an endorsement by The Booth.
        </p>
      </section>

      <section id="ai">
        <h2>9. AI-generated content disclaimer</h2>
        <p>
          The Services use artificial intelligence to generate fantasy sports recaps and insights. You
          acknowledge and agree that:
        </p>
        <ul>
          <li>
            AI-generated content is provided for <strong>informational and entertainment purposes only</strong>
          </li>
          <li>AI-generated content may contain errors, inaccuracies, or outdated information</li>
          <li>You should not rely solely on AI-generated content when making decisions of any kind</li>
          <li>
            The Booth makes no representations or warranties regarding the accuracy, completeness, or fitness
            for any particular purpose of AI-generated content
          </li>
        </ul>
      </section>

      <section id="fantasy">
        <h2>10. Fantasy sports disclaimer</h2>
        <p>
          The Booth is a fantasy sports <strong>coverage and entertainment service</strong> only. We do not
          operate, host, or facilitate any fantasy sports contests, daily fantasy sports platforms, or gambling
          of any kind.
        </p>
        <p>
          We are not affiliated with, endorsed by, or sponsored by ESPN or any sports league. Fantasy sports
          data and statistics provided through the Services are sourced from third-party APIs and may not
          always be accurate or up to date. The Booth is not responsible for any outcomes, losses, or decisions
          arising from participation in fantasy sports based on content provided by the Services.
        </p>
      </section>

      <section id="warranties">
        <h2>11. Disclaimer of warranties</h2>
        <p>
          THE SERVICES ARE PROVIDED ON AN "<strong>AS IS</strong>" AND "<strong>AS AVAILABLE</strong>" BASIS
          WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, WE
          DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS
          FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
        </p>
        <p>
          WE DO NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, SECURE, OR FREE OF VIRUSES OR
          OTHER HARMFUL COMPONENTS. YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK.
        </p>
      </section>

      <section id="liability">
        <h2>12. Limitation of liability</h2>
        <p>
          TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL THE BOOTH, ITS OFFICERS,
          DIRECTORS, EMPLOYEES, OR AGENTS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
          PUNITIVE DAMAGES — INCLUDING LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES — ARISING OUT
          OF OR IN CONNECTION WITH YOUR ACCESS TO OR USE OF (OR INABILITY TO ACCESS OR USE) THE SERVICES, EVEN
          IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          IN NO EVENT SHALL OUR TOTAL CUMULATIVE LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THE
          SERVICES EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE
          CLAIM OR (B) ONE HUNDRED DOLLARS ($100.00).
        </p>
      </section>

      <section id="indemnification">
        <h2>13. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless The Booth and its officers, directors, employees,
          contractors, and agents from and against any claims, liabilities, damages, losses, costs, or expenses
          (including reasonable attorneys' fees) arising out of or in any way connected with:
        </p>
        <ul>
          <li>Your access to or use of the Services</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any third-party rights, including privacy rights or intellectual property rights</li>
          <li>Any content you submit to the Services</li>
        </ul>
      </section>

      <section id="termination">
        <h2>14. Termination</h2>
        <p>
          We reserve the right to suspend or terminate your access to the Services at any time, with or without
          cause or notice, including for violations of these Terms. Upon termination, your right to use the
          Services will immediately cease.
        </p>
        <p>
          You may terminate your account at any time by contacting us at{" "}
          <a rel="noopener noreferrer" target="_blank" href="mailto:admin@thebooth.gg">
            admin@thebooth.gg
          </a>
          . Termination of your account does not entitle you to a refund of any prepaid subscription fees,
          except as required by applicable law.
        </p>
        <p>
          All provisions of these Terms that by their nature should survive termination — including ownership
          provisions, warranty disclaimers, indemnity, and limitations of liability — shall survive.
        </p>
      </section>

      <section id="governing-law">
        <h2>15. Governing law and dispute resolution</h2>
        <p>
          These Terms and any disputes arising out of or related to them or the Services shall be governed by
          and construed in accordance with the laws of the State of Washington and applicable federal law,
          without regard to its conflict of law provisions.
        </p>
        <p>
          You agree that any legal action or proceeding arising under these Terms shall be brought exclusively
          in the federal or state courts located in Washington, and you consent to personal jurisdiction and
          venue in such courts.
        </p>
      </section>

      <section id="changes">
        <h2>16. Changes to terms</h2>
        <p>
          We reserve the right to modify these Terms at any time. When we make changes, we will update the
          "Last updated" date at the top of this page. For material changes, we will provide additional notice,
          such as by sending an email to the address associated with your account or by displaying a prominent
          notice within the Services.
        </p>
        <p>
          Your continued use of the Services after the effective date of any revised Terms constitutes your
          acceptance of the changes. If you do not agree to the revised Terms, you must stop using the Services.
        </p>
      </section>

      <section id="contact">
        <h2>17. Contact us</h2>
        <p>If you have any questions about these Terms, please contact us at:</p>
        <p>
          <strong>The Booth</strong>
          <br />
          Email:{" "}
          <a rel="noopener noreferrer" target="_blank" href="mailto:admin@thebooth.gg">
            admin@thebooth.gg
          </a>
        </p>
      </section>
    </article>
  );
}

export default TermsOfServiceContent;
